import { trans } from '@lingoda/i18n';
import {
    type Path,
    postCheckoutBookClassPath,
    postCheckoutConfirmLanguagePath,
    postCheckoutOnboardingSurveyPath,
    postCheckoutTimeSlotsPath,
} from '@lingoda/router';
import { assert } from '@lingoda/utils';
import type { OptionGroupOption } from './components';

export type SurveyStep = (typeof SURVEY_STEPS)[number];
export type SurveyStepKey = SurveyStep['questionKey'];
type RevealQuestionKey =
    | 'move_timeline'
    | 'industry'
    | 'role'
    | 'field_of_study'
    | 'current_level_path'
    | 'current_sublevel';
type DerivedAnswerKey =
    | 'study_hours_per_week'
    | 'classes_per_week'
    | 'self_study_per_week'
    | 'commitment_reason'
    | 'commitment_committed';
export type QuestionKey = SurveyStepKey | RevealQuestionKey | DerivedAnswerKey;

export type Answers = Partial<Record<QuestionKey, string>>;

export const SKIPPED_ANSWER = 'skipped';

export const getInitialAnswer = (answers: Answers, questionKey: QuestionKey): string => {
    const stored = answers[questionKey];

    return !stored || stored === SKIPPED_ANSWER ? '' : stored;
};

const MAIN_REASON_VALUES = [
    'career',
    'travel',
    'relocation',
    'culture',
    'family',
    'brain',
    'education',
    'other',
] as const;

export type MainReason = (typeof MAIN_REASON_VALUES)[number];

export const isMainReason = (value: string): value is MainReason =>
    MAIN_REASON_VALUES.some((reason) => reason === value);

export const getMainReasons = (): OptionGroupOption[] => {
    return [
        {
            value: 'career',
            emoji: '💼',
            title: trans('onboarding-main-reason-career-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-career-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'travel',
            emoji: '✈️',
            title: trans('onboarding-main-reason-travel-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-travel-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'relocation',
            emoji: '🏡',
            title: trans('onboarding-main-reason-relocation-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-relocation-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'culture',
            emoji: '🎭',
            title: trans('onboarding-main-reason-culture-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-culture-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'family',
            emoji: '👨‍👩‍👧',
            title: trans('onboarding-main-reason-family-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-family-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'brain',
            emoji: '🧠',
            title: trans('onboarding-main-reason-brain-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-brain-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'education',
            emoji: '🎓',
            title: trans('onboarding-main-reason-education-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-education-desc', {}, 'student-post-checkout'),
        },
        {
            value: 'other',
            emoji: '💬',
            title: trans('onboarding-main-reason-other-label', {}, 'student-post-checkout'),
            subtitle: trans('onboarding-main-reason-other-desc', {}, 'student-post-checkout'),
        },
    ];
};

export const SURVEY_STEPS = [
    { questionKey: 'primary_motivation', skippable: false },
    { questionKey: 'sub_motivation', skippable: true },
    { questionKey: 'situational_context', skippable: true },
    { questionKey: 'immersion_status', skippable: true },
    { questionKey: 'occupation', skippable: false },
    { questionKey: 'current_level', skippable: false },
    { questionKey: 'target_level', skippable: false },
    { questionKey: 'study_formats', skippable: true },
    { questionKey: 'timeline', skippable: false },
    { questionKey: 'pledge', skippable: true },
] as const;

// The pledge step is gated by the onboarding_m2_pledge flag; the rest are always present.
export const getActiveSurveySteps = (isPledgeEnabled: boolean): readonly SurveyStep[] => {
    return isPledgeEnabled
        ? SURVEY_STEPS
        : SURVEY_STEPS.filter((step) => step.questionKey !== 'pledge');
};

export const getStepsToSkip = (steps: readonly SurveyStep[], fromIndex: number) => {
    let nextIndex = fromIndex;

    while (nextIndex < steps.length && steps[nextIndex].skippable) {
        nextIndex++;
    }

    return {
        skippedKeys: steps.slice(fromIndex, nextIndex).map((step) => step.questionKey),
        nextIndex,
    };
};

interface OnboardingStepSlot {
    path: Path;
    size: number;
}

const getOnboardingFlow = (isInTrial: boolean, surveyStepCount: number): OnboardingStepSlot[] => {
    const languageConfirmation: OnboardingStepSlot[] = isInTrial
        ? [{ path: postCheckoutConfirmLanguagePath(), size: 1 }]
        : [];

    return [
        ...languageConfirmation,
        { path: postCheckoutOnboardingSurveyPath(), size: surveyStepCount },
        { path: postCheckoutTimeSlotsPath(), size: 1 },
        { path: postCheckoutBookClassPath(), size: 1 },
    ];
};

export const getOnboardingTotalSteps = (isInTrial: boolean, surveyStepCount: number): number => {
    return getOnboardingFlow(isInTrial, surveyStepCount).reduce(
        (total, slot) => total + slot.size,
        0,
    );
};

export const getOnboardingStepByPathname = (
    pathname: string,
    isInTrial: boolean,
    surveyStepCount: number,
): number | null => {
    const flow = getOnboardingFlow(isInTrial, surveyStepCount);
    const index = flow.findIndex((slot) => pathname.startsWith(slot.path.toString()));

    if (index === -1) {
        return null;
    }

    return flow.slice(0, index).reduce((total, slot) => total + slot.size, 0) + 1;
};

export const getOnboardingSurveyStartStep = (
    isInTrial: boolean,
    surveyStepCount: number,
): number => {
    const step = getOnboardingStepByPathname(
        postCheckoutOnboardingSurveyPath().toString(),
        isInTrial,
        surveyStepCount,
    );
    assert(step !== null, 'Onboarding survey is missing from the flow');

    return step;
};
